import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { 
  chatWebSocketService, 
  WebSocketConnectionState, 
  WebSocketEventHandlers,
  WebSocketChatMessage,
  WebSocketTypingEvent,
  WebSocketStatusEvent,
  WebSocketErrorEvent
} from '../services/chatWebSocketService';
import { ChatMessage } from '../services/chatSupportService';
import { formatMessageText } from '../components/ChatSupport/utils/messageUtils';

interface ChatWebSocketContextType {
  // Connection state
  connectionState: WebSocketConnectionState;
  isConnected: boolean;
  currentSessionId: string | null;
  
  // Connection management
  connect: (sessionId: string) => Promise<void>;
  disconnect: () => void;
  
  // Message handling
  messages: ChatMessage[];
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void;
  
  // Notification system
  unreadCount: number;
  hasNewMessages: boolean;
  markMessagesAsRead: () => void;
  resetNotificationState: () => void;
  
  // Typing indicators
  isTyping: boolean;
  sendTyping: (isTyping: boolean) => void;
  
  // Error handling
  lastError: string | null;
  clearError: () => void;
  
  // Chat open state management
  setChatOpen: (isOpen: boolean, closeFunction?: () => void) => void;
  closeChatWindow: () => void;
  
  // Session management
  clearChatSession: () => void;
  sessionCleared: boolean;
  resetSessionClearedFlag: () => void;
}

const ChatWebSocketContext = createContext<ChatWebSocketContextType | undefined>(undefined);

interface ChatWebSocketProviderProps {
  children: React.ReactNode;
}

export const ChatWebSocketProvider: React.FC<ChatWebSocketProviderProps> = ({ children }) => {
  // Connection state
  const [connectionState, setConnectionState] = useState<WebSocketConnectionState>('disconnected');
  const [isConnected, setIsConnected] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  
  // Message state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  
  // Typing state
  const [isTyping, setIsTyping] = useState(false);
  
  // Error state
  const [lastError, setLastError] = useState<string | null>(null);
  
  // Session cleared flag
  const [sessionCleared, setSessionCleared] = useState(false);
  
  // Refs for tracking
  const isChatOpenRef = useRef(false);
  const lastReadMessageIdRef = useRef<string | null>(null);
  const hasConnectedRef = useRef(false);
  const closeChatWindowRef = useRef<(() => void) | null>(null);

  // Update connection state
  const handleConnectionStateChange = useCallback((state: WebSocketConnectionState) => {
    console.log('🔌 Global WebSocket connection state changed:', state);
    setConnectionState(state);
    setIsConnected(state === 'connected');
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback((wsMessage: WebSocketChatMessage) => {
    console.log('🔌 Global WebSocket message received:', wsMessage);
    
    // Convert WebSocket message to ChatMessage format
    const chatMessage: ChatMessage = {
      id: wsMessage.id || `${wsMessage.sender}_${Date.now()}`,
      sessionId: wsMessage.sessionId,
      message: formatMessageText(wsMessage.message), // Apply formatting to WebSocket messages
      sender: wsMessage.sender,
      timestamp: wsMessage.timestamp,
      isRead: wsMessage.isRead
    };
    
    // Add message to state with improved deduplication
    setMessages(prev => {
      // Simple and reliable duplicate detection using only message ID
      const exists = prev.some(msg => msg.id === chatMessage.id);
      
      if (exists) {
        console.log('🔌 Duplicate message detected by ID, skipping:', chatMessage.id);
        return prev;
      }
      
      console.log('🔌 Adding new WebSocket message:', chatMessage);
      return [...prev, chatMessage];
    });
    
    // Update notification state if chat is not open or message is from support
    if (!isChatOpenRef.current && chatMessage.sender === 'support') {
      setUnreadCount(prev => prev + 1);
      setHasNewMessages(true);
    }
  }, []);

  // Handle typing events
  const handleTyping = useCallback((event: WebSocketTypingEvent) => {
    console.log('🔌 Global WebSocket typing event received:', event.data);
    setIsTyping(event.data.isTyping);
  }, []);

  // Handle status events
  const handleStatus = useCallback((event: WebSocketStatusEvent) => {
    console.log('🔌 Global WebSocket status event received:', event.data);
  }, []);

  // Handle error events
  const handleError = useCallback((event: WebSocketErrorEvent) => {
    console.error('🔌 Global WebSocket error received:', event.data);
    setLastError(event.data.error);
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async (sessionId: string) => {
    if (currentSessionId === sessionId && isConnected) {
      console.log('🔌 Already connected to session:', sessionId);
      return;
    }

    setCurrentSessionId(sessionId);
    
    const handlers: WebSocketEventHandlers = {
      onMessage: handleMessage,
      onTyping: handleTyping,
      onStatus: handleStatus,
      onError: handleError,
      onConnectionStateChange: handleConnectionStateChange
    };

    try {
      await chatWebSocketService.connect(sessionId, handlers);
      hasConnectedRef.current = true;
      console.log('🔌 Global WebSocket connected successfully for session:', sessionId);
    } catch (error) {
      console.error('🔌 Failed to connect global WebSocket:', error);
      setLastError(error instanceof Error ? error.message : 'Failed to connect');
    }
  }, [currentSessionId, isConnected, handleMessage, handleTyping, handleStatus, handleError, handleConnectionStateChange]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting global WebSocket');
    chatWebSocketService.disconnect();
    setCurrentSessionId(null);
    setIsConnected(false);
    hasConnectedRef.current = false;
  }, []);

  // Add message manually (for REST API messages)
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => {
      // Simple and reliable duplicate detection using only message ID
      const exists = prev.some(msg => msg.id === message.id);
      
      if (exists) {
        console.log('🔌 Duplicate message detected by ID in addMessage, skipping:', message.id);
        return prev;
      }
      
      console.log('🔌 Adding new message via addMessage:', message);
      return [...prev, message];
    });
  }, []);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setUnreadCount(0);
    setHasNewMessages(false);
    lastReadMessageIdRef.current = null;
  }, []);

  // Mark messages as read
  const markMessagesAsRead = useCallback(() => {
    setUnreadCount(0);
    setHasNewMessages(false);
    
    // Update the last read message ID
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      lastReadMessageIdRef.current = lastMessage.id;
    }
  }, [messages]);

  // Reset notification state
  const resetNotificationState = useCallback(() => {
    setUnreadCount(0);
    setHasNewMessages(false);
    lastReadMessageIdRef.current = null;
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((typing: boolean) => {
    if (!isConnected) {
      return;
    }
    
    chatWebSocketService.sendTyping(typing);
  }, [isConnected]);

  // Clear error
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Expose chat open state management
  const setChatOpen = useCallback((isOpen: boolean, closeFunction?: () => void) => {
    isChatOpenRef.current = isOpen;
    
    // Register the close function if provided
    if (closeFunction) {
      closeChatWindowRef.current = closeFunction;
    }
    
    // If chat is opened, mark messages as read
    if (isOpen) {
      markMessagesAsRead();
    }
  }, [markMessagesAsRead]);

  // Close chat window function
  const closeChatWindow = useCallback(() => {
    console.log('🔌 Closing chat window from global context');
    if (closeChatWindowRef.current) {
      closeChatWindowRef.current();
    }
  }, []);

  // Clear chat session completely (for logout or reset)
  const clearChatSession = useCallback(() => {
    console.log('🔌 Clearing chat session completely');
    
    // Set session cleared flag to notify components
    setSessionCleared(true);
    
    // Close chat window first
    closeChatWindow();
    
    // Disconnect WebSocket
    disconnect();
    
    // Clear ALL localStorage chat session data
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_session_data');
    
    // Clear any other potential chat-related localStorage items
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('chat') || key.includes('Chat') || key.includes('session'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      console.log('🔌 Removing chat-related localStorage key:', key);
      localStorage.removeItem(key);
    });
    
    // Clear all messages and reset state
    clearMessages();
    resetNotificationState();
    
    // Reset connection state
    setCurrentSessionId(null);
    setIsConnected(false);
    hasConnectedRef.current = false;
    
    // Clear any remaining refs
    lastReadMessageIdRef.current = null;
    isChatOpenRef.current = false;
    closeChatWindowRef.current = null;
    
    console.log('🔌 Chat session completely removed and cleared');
  }, [closeChatWindow, disconnect, clearMessages, resetNotificationState]);

  // Reset session cleared flag (for new session initialization)
  const resetSessionClearedFlag = useCallback(() => {
    console.log('🔌 Resetting session cleared flag for new session');
    setSessionCleared(false);
  }, []);

  // Initialize connection if session exists in localStorage
  useEffect(() => {
    const initializeConnection = async () => {
      const storedSessionId = localStorage.getItem('chat_session_id');
      const storedSessionData = localStorage.getItem('chat_session_data');
      
      if (storedSessionId && storedSessionData && !hasConnectedRef.current) {
        console.log('🔌 Found existing session in localStorage, connecting WebSocket:', storedSessionId);
        try {
          // Test connection first
          const testResult = await chatWebSocketService.testConnection(storedSessionId);
          if (testResult.success) {
            await connect(storedSessionId);
          } else {
            console.warn('🔌 Existing session connection test failed:', testResult.error);
          }
        } catch (error) {
          console.error('🔌 Failed to connect to existing session:', error);
        }
      }
    };

    initializeConnection();
  }, [connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('🔌 Cleaning up global WebSocket connection');
      disconnect();
    };
  }, [disconnect]);

  const contextValue: ChatWebSocketContextType = {
    // Connection state
    connectionState,
    isConnected,
    currentSessionId,
    
    // Connection management
    connect,
    disconnect,
    
    // Message handling
    messages,
    addMessage,
    clearMessages,
    
    // Notification system
    unreadCount,
    hasNewMessages,
    markMessagesAsRead,
    resetNotificationState,
    
    // Typing indicators
    isTyping,
    sendTyping,
    
    // Error handling
    lastError,
    clearError,
    
    // Chat open state management
    setChatOpen,
    closeChatWindow,
    
    // Session management
    clearChatSession,
    sessionCleared,
    resetSessionClearedFlag,
  };

  return (
    <ChatWebSocketContext.Provider value={contextValue}>
      {children}
    </ChatWebSocketContext.Provider>
  );
};

export const useChatWebSocket = (): ChatWebSocketContextType => {
  const context = useContext(ChatWebSocketContext);
  if (context === undefined) {
    throw new Error('useChatWebSocket must be used within a ChatWebSocketProvider');
  }
  return context;
};

